import { Drawer } from "@material-tailwind/react";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Router from "../../../routes/Router";
import { AppRoutes } from "../../../routes/app-routes";
import { useLogout } from "../../../store/features/authentication/actions";
import { useAuthenticationState } from "../../../store/features/authentication/state";
import { mtProps } from "../../../util/mtProps";
import { Sidebar } from "../../sidebar/Sidebar";
import { Header, UserMenu } from "../components/Header";
import { config } from "../../../config";
import LocalStorage from "../../../util/storage";
import { useLogPageViewMutation } from "../../../store/api/adminApi";
import { useGetCustomToolsQuery } from "../../../store/api/customToolApi";

const LayoutContainer = () => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const location = useLocation();
	const lastLoggedPath = useRef("");

	const { logout } = useLogout();
	const { state } = useAuthenticationState();
	const isAuthenticated = state.isAuthenticated();
	const profile = state.profile();
	const [logPageView] = useLogPageViewMutation();
	const { data: customToolsData } = useGetCustomToolsQuery(undefined, { skip: !isAuthenticated });

	// Log page views on navigation
	useEffect(() => {
		if (!isAuthenticated) return;
		const path = location.pathname;
		if (path === lastLoggedPath.current) return;
		lastLoggedPath.current = path;

		// Map route to tool info
		const routes = Object.values(AppRoutes);
		const matchedRoute = routes.find((r) => r.url === path);

		// Custom tool pages: /tools/:slug
		if (path.startsWith("/tools/")) {
			const slug = path.split("/")[2];
			const tool = customToolsData?.tools?.find((t: any) => t.slug === slug);
			logPageView({ page: path, toolSlug: slug || "custom-tools", toolName: tool?.name || slug });
			return;
		}

		// Chat pages: /chat/:id
		if (path.startsWith("/chat/")) {
			logPageView({ page: path, toolSlug: "ai-assistant", toolName: "Ask Scout" });
			return;
		}

		if (matchedRoute) {
			logPageView({
				page: path,
				toolSlug: matchedRoute.toolSlug || matchedRoute.id,
				toolName: matchedRoute.name
			});
		}
	}, [location.pathname, isAuthenticated, logPageView, customToolsData]);

	// Check Google OAuth scopes on mount
	useEffect(() => {
		if (!isAuthenticated) return;
		const checkScopes = async () => {
			try {
				const authData = LocalStorage.get("authData");
				if (!authData?.token) return;
				const res = await fetch(`${config.apiUrl}/auth/check-scopes`, {
					headers: { Authorization: `Bearer ${authData.token}` }
				});
				if (!res.ok) return;
				const data = await res.json();
				if (!data.valid && (data.reason === "missing_scopes" || data.reason === "no_token" || data.reason === "token_invalid")) {
					// Force re-login to get updated scopes
					alert("Your session needs updated permissions. Please log in again.");
					logout();
				}
			} catch {
				// Silent fail — don't block the app
			}
		};
		checkScopes();
	}, [isAuthenticated, logout]);

	const currentRoute = Object.values(AppRoutes).find(
		(route) => route.url === location.pathname
	);
	const showMenu = currentRoute?.showMenu !== false;

	if (!isAuthenticated) {
		return <Router />;
	}

	return (
		<div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
			>
				Skip to main content
			</a>

			<Header
				showMenuButton={showMenu}
				onMenuClick={() => setDrawerOpen(true)}
				rightContent={<UserMenu profile={profile} onLogout={logout} />}
			/>

			<div className="flex flex-1 overflow-hidden">
				{showMenu && (
					<div className="hidden lg:block h-full">
						<Sidebar />
					</div>
				)}

				{showMenu && (
					<Drawer
						{...mtProps}
						open={drawerOpen}
						onClose={() => setDrawerOpen(false)}
						className="lg:hidden !w-auto !z-[9999]"
						aria-label="Mobile navigation menu"
					>
						<Sidebar onNavClick={() => setDrawerOpen(false)} />
					</Drawer>
				)}

				<main
					id="main-content"
					className="flex-1 overflow-auto bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800/50"
					aria-label="Main content"
				>
					<Router />
				</main>
			</div>
		</div>
	);
};

export default LayoutContainer;
